import type { DealCaptureArea, DealFlow } from "@/types/deal-capture";

/**
 * Deal Capture's urls, and what the sidebar reads back out of them.
 *
 * **The url is the sidebar's state.** The area, the deal, contract or entity
 * in scope and the deal flow are all read from the location, never kept in a
 * store. That is what makes "choosing Deal Capture clears selections and
 * opens the landing" true by construction: the module switcher links to
 * `DC_PATH`, and a url with no area and no record selects nothing.
 */
export const DC_PATH = "/deal-capture";

/** The query param the Integrated / Non-Integrated toggle writes. */
export const FLOW_PARAM = "flow";

export const AREAS: ReadonlyArray<{ key: DealCaptureArea; label: string; crumb: string }> = [
  { key: "deals", label: "Deals", crumb: "Deals" },
  { key: "contracts", label: "Contracts", crumb: "Contracts" },
  { key: "entities", label: "Entities", crumb: "Entities" },
  // "Deleted Deals" in the picker, "Deleted deals" in the trail: the spec
  // writes it both ways and each spot keeps its own.
  { key: "deleted", label: "Deleted Deals", crumb: "Deleted deals" },
];

export function areaLabel(area: DealCaptureArea): string {
  return AREAS.find((a) => a.key === area)?.label ?? area;
}

/** An area's Summary page. */
export function areaPath(area: DealCaptureArea): string {
  return `${DC_PATH}/${area}`;
}

/** An area's full list ("All deals", "All contracts", …). */
export function areaListPath(area: DealCaptureArea): string {
  return `${areaPath(area)}/list`;
}

export const NEW_DEAL_PATH = `${DC_PATH}/deals/new`;

export function dealPath(dealNumber: string): string {
  return `${DC_PATH}/deals/${encodeURIComponent(dealNumber)}`;
}

export function contractPath(contractId: string): string {
  return `${DC_PATH}/contracts/${encodeURIComponent(contractId)}`;
}

export function entityPath(entityId: string): string {
  return `${DC_PATH}/entities/${encodeURIComponent(entityId)}`;
}

/** Path segments under an area that are pages, not record ids. */
const AREA_PAGES = new Set(["list", "new"]);

/** Where a pathname sits in Deal Capture: its area, and the record open in it. */
export interface DealCaptureLocation {
  area: DealCaptureArea | null;
  /** The deal number, contract id or entity id in the url, if any. */
  recordId: string | null;
  /** `list`, `new`, or `null` for the area's Summary or a record page. */
  page: "list" | "new" | null;
}

export function locate(pathname: string): DealCaptureLocation {
  const none: DealCaptureLocation = { area: null, recordId: null, page: null };
  if (pathname !== DC_PATH && !pathname.startsWith(`${DC_PATH}/`)) return none;
  const [areaSegment, second] = pathname.slice(DC_PATH.length + 1).split("/");
  const area = AREAS.find((a) => a.key === areaSegment)?.key ?? null;
  if (!area) return none;
  if (!second) return { area, recordId: null, page: null };
  if (AREA_PAGES.has(second)) {
    return { area, recordId: null, page: second as "list" | "new" };
  }
  return { area, recordId: decodeSegment(second), page: null };
}

/**
 * A path segment, decoded. A malformed escape (`%E0%A4%A`) keeps its raw
 * form rather than throwing: `locate` runs in the sidebar on every render, so
 * a bad url must reach the page's not-found state, not take down the shell.
 */
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** The deal flow in scope; Integrated unless the url says otherwise. */
export function flowOf(search: string): DealFlow {
  return new URLSearchParams(search).get(FLOW_PARAM) === "non_integrated"
    ? "non_integrated"
    : "integrated";
}

/**
 * `path` with the current deal flow carried over, for links that stay in the
 * Deals area. `path` may already have a query string.
 */
export function keepFlow(path: string, search: string): string {
  if (flowOf(search) === "integrated") return path;
  return `${path}${path.includes("?") ? "&" : "?"}${FLOW_PARAM}=non_integrated`;
}

/**
 * A page section the sidebar can jump to. `id` is the element's DOM id; the
 * link scrolls to it and changes nothing else on the page.
 */
export interface JumpTarget {
  id: string;
  label: string;
}

/**
 * The deal page's sections, in page order, grouped as the sidebar shows them.
 * The deal page renders an element with each id, so the two cannot drift.
 */
export const DEAL_JUMP_GROUPS: ReadonlyArray<{ label: string; id: string; items: JumpTarget[] }> = [
  {
    label: "Contract details",
    id: "tp-contract",
    items: [
      { id: "tp-accounts", label: "Entities" },
      { id: "tp-documents", label: "Contract details" },
    ],
  },
  {
    label: "Term and pricing",
    id: "tp-section",
    items: [
      { id: "tp-term", label: "Term" },
      { id: "tp-price", label: "Contract price" },
      { id: "tp-terms", label: "Deal terms" },
      { id: "tp-delivery", label: "Delivery points" },
    ],
  },
];

export const DEAL_TRAILING_JUMPS: ReadonlyArray<JumpTarget> = [
  { id: "tp-related", label: "Related deals" },
  { id: "tp-activity", label: "Activity" },
];

export const DEAL_TOP_ID = "tp-top";

/** The contract page's sections, in page order. */
export const CONTRACT_JUMPS: ReadonlyArray<JumpTarget> = [
  { id: "ct-top", label: "Overview" },
  { id: "ct-deals", label: "Deals" },
  { id: "ct-parties", label: "Counterparties" },
  { id: "ct-delivery", label: "Delivery points" },
  { id: "ct-attention", label: "Needs attention" },
];
